import { useTranslation } from 'react-i18next'
import { FullscreenModal } from '@/components/global/FullscreenModal/FullscreenModal'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'
import { disarmUnsavedChangesWarning } from '@/hooks/editor/useUnsavedChangesWarning'

type EditorPageSetTurnedOverModalProps = {
  show: boolean
  /** Close the prompt and go back to the canvas. */
  onDismiss: () => void
  /** Load the editor again; the editor passes a full page reload. */
  reload: () => void
}

/**
 * Shown when a Save, or a turnover of the page set, is refused because the
 * page set was turned over to Lotus text boxes after this tab loaded it. Reload removes the browser's leave
 * prompt first, since the copy has already said this tab's unsaved edits are
 * lost, then loads the editor again. Dismiss goes back to the canvas so the
 * person can copy text out; it leaves the unsaved flag alone, so Back still
 * asks and the next Save raises this prompt again.
 */
export const EditorPageSetTurnedOverModal = ({
  show,
  onDismiss,
  reload,
}: EditorPageSetTurnedOverModalProps) => {
  const { t } = useTranslation()
  if (!show) return null

  const reloadEditor = () => {
    disarmUnsavedChangesWarning()
    reload()
  }

  return (
    <FullscreenModal show={[true, onDismiss]} hideClose={false}>
      <Flex
        column
        full
        padding="32px"
        maxWidth="600px"
        data-testid="editor-page-set-turned-over-modal"
      >
        <Text size="20px" weight="600" margin="10px 0 10px 0">
          {t('editor.pageSetTurnedOverModal.title')}
        </Text>
        <Text size="16px" margin="10px 0 10px 0">
          {t('editor.pageSetTurnedOverModal.message')}
        </Text>
        <Text size="16px" weight="500" margin="10px 0 10px 0">
          {t('editor.pageSetTurnedOverModal.editsLost')}
        </Text>
        <Flex justify="flex-end" align="center" margin="10px 0 0 0">
          <Button
            margin="0 16px 0 0"
            onClick={onDismiss}
            data-testid="editor-page-set-turned-over-dismiss"
          >
            {t('editor.pageSetTurnedOverModal.dismiss')}
          </Button>
          <Button
            primary
            onClick={reloadEditor}
            data-testid="editor-page-set-turned-over-reload"
          >
            {t('editor.pageSetTurnedOverModal.reload')}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}
